import React, { useContext, type FC, type PropsWithChildren, useState } from 'react';

import { Box, Heading, Text, Link, LayoutContext, ImageWithPlaceholder, type HeadingTagsType } from '@lucid/components';
import { withImage, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from 'types/types';

export type PersonColor = Exclude<Lucid.Theme.Color.Keys, 'white' | 'yellow' | 'lime'>;

export interface PersonProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Set to show the company name - otherwise hide it */
  willShowCompany: boolean;
  /** Sets the color scheme for the people backgrounds */
  backgroundColor?: PersonColor;
  /** Changes the text color for a dark section background */
  isDarkBackground?: boolean;
  /** Controls whether the bio should render */
  shouldHideBio?: boolean;
  /** Controls whether the additional 'View bio' button should render */
  hasVisibleBioLink?: boolean;
  /** State from parent to know whether this bio is currently displayed */
  expandedIndex?: number;
  /** The position of this person in the parent section */
  index: number;
  /** Function to set the expanded index state in the parent component */
  setExpandedIndex: React.Dispatch<React.SetStateAction<number>>;
  /** The normalized person data to be displayed */
  person: PersonGeneric;
  /** The heading level for the Person name */
  headingLevel?: HeadingTagsType;
  /** Used to externally  manage modal state*/
  isModalOpen?: boolean;
}

export type PersonGeneric = Lucid.Directus.Generated.Person_Translations & {
  id?: string;
  name: string;
  image: Lucid.Directus.Generated.Directus_Files;
  company?: string;
  bio?: string;
  title?: string;
  bioDetails?: { i18nKey: string; value: string | React.JSX.Element };
};

const Person: FC<PropsWithChildren<PersonProps>> = ({
  className,
  person,
  isDarkBackground,
  willShowCompany,
  expandedIndex,
  hasVisibleBioLink = false,
  index,
  setExpandedIndex,
  shouldHideBio = false,
  headingLevel = 'h3',
  isModalOpen,
}) => {
  const [hasHover, setHasHover] = useState(false);
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_homepage_learn_more_about', 'mktg_leadership_view_bio'],
    shortCode,
  });
  const { name, image, company, title } = person;
  const isSelected = expandedIndex === index;
  const shouldShowLinkText = hasVisibleBioLink && !shouldHideBio;

  const handleOnClick = () => {
    setExpandedIndex(isSelected ? -1 : index);
  };

  return (
    <Box
      as="figure"
      css={theme => ({
        margin: '0 auto',
        maxWidth: '277px',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:focus-within': {
          outline: `2px solid ${isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100]}`,
          outlineOffset: theme.space.s4,
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      id={`bio-${person.id}`}
      onMouseLeave={() => setHasHover(false)}
      onMouseOver={() => setHasHover(true)}
    >
      <Box
        css={theme => [
          {
            // TODO: an incoming request from design will add 12px to theme space values
            marginBottom: '12px',
            position: 'relative',
          },
          hasHover &&
            !shouldHideBio && {
              '@media (hover: hover) and (pointer: fine)': {
                '&::before': {
                  content: '""',
                  fontWeight: theme.fontWeights.fw600,
                  justifyContent: 'center',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: theme.colors.coolGrey[100],
                  opacity: 0.7,
                  zIndex: theme.zIndices.z10,
                },
              },
            },
        ]}
      >
        {hasHover && !shouldHideBio && (
          <Text
            css={theme => ({
              display: 'none',
              '@media (hover: hover) and (pointer: fine)': {
                display: 'block',
                color: theme.colors.white,
                position: 'absolute',
                zIndex: theme.zIndices.z100,
                top: 'calc(50% - 12px)',
                textAlign: 'center',
                width: '100%',
                margin: 0,
              },
            })}
            variant="interactiveM700"
          >
            {withSmartling('mktg_leadership_view_bio')}
          </Text>
        )}
        <ImageWithPlaceholder
          isBackgroundImage
          alt={`${name} headshot`}
          aspectRatio="1:1"
          css={{
            div: {
              backgroundPosition: 'top',
            },
          }}
          src={withImage(image)}
        />
      </Box>
      <figcaption css={theme => shouldShowLinkText && { marginBottom: theme.space.s32 }}>
        <Heading
          as={headingLevel}
          css={theme => ({
            marginBottom: theme.space.s8,
            color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
          })}
          variant="headingM"
        >
          {name}
        </Heading>
        {title && (
          <Text
            css={theme => ({
              marginBottom: theme.space.s8,
              color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
            })}
            variant="paragraphS400"
          >
            {title}
          </Text>
        )}
        {willShowCompany && company && (
          <Text
            css={theme => ({
              marginBottom: theme.space.s8,
              color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
            })}
            variant="labelXS400"
          >
            {company}
          </Text>
        )}
      </figcaption>
      <Link
        asButton
        aria-disabled={shouldHideBio || !(person.bio || person.bioDetails)}
        aria-expanded={isSelected}
        aria-haspopup="dialog"
        aria-hidden={isModalOpen}
        aria-label={withSmartling('mktg_homepage_learn_more_about', { title: name })}
        css={theme => ({
          position: 'absolute',
          height: '100%',
          width: '100%',
          top: 0,
          left: 0,
          zIndex: theme.zIndices.z100,
          display: 'flex',
          alignItems: 'flex-end',
          '&:focus': {
            outline: 'none',
            p: {
              textDecoration: 'underline',
            },
          },
          '&:disabled': {
            pointerEvents: 'none',
          },
        })}
        isDisabled={shouldHideBio || !(person.bio || person.bioDetails)}
        onClick={handleOnClick}
        variant="basic"
      >
        {shouldShowLinkText && (
          <Text
            as="p"
            css={theme => [
              {
                color: theme.colors.blue[70],
                fontWeight: theme.fontWeights.fw600,
                margin: 0,
              },
              hasHover && {
                textDecoration: 'underline',
              },
            ]}
            variant="interactiveS700"
          >
            {withSmartling('mktg_leadership_view_bio')}
          </Text>
        )}
      </Link>
    </Box>
  );
};

export { Person };
export default Person;
